const STORAGE_KEY = 'zion_unread_counts'

const listeners = new Set<() => void>()

function loadMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: Record<string, number> = {}
    for (const [key, value] of Object.entries(parsed)) {
      const n = Number(value)
      if (key.trim() && Number.isFinite(n) && n > 0) out[key.trim().toLowerCase()] = Math.floor(n)
    }
    return out
  } catch {
    return {}
  }
}

function saveMap(map: Record<string, number>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    /* quota — ignora */
  }
}

function notifyChanged(): void {
  syncAppBadge()
  for (const fn of listeners) fn()
}

export function onUnreadChanged(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getUnread(contactId: string): number {
  const key = contactId.trim().toLowerCase()
  if (!key) return 0
  return loadMap()[key] ?? 0
}

export function getTotalUnread(): number {
  return Object.values(loadMap()).reduce((sum, n) => sum + n, 0)
}

/** Alias richiesto dal MASTER FIX. */
export function calculateTotalUnread(): number {
  return getTotalUnread()
}

export function formatUnreadCount(count: number): string {
  if (count <= 0) return ''
  return count > 99 ? '99+' : String(count)
}

export function incrementUnread(contactId: string, by = 1): number {
  const key = contactId.trim().toLowerCase()
  if (!key || by <= 0) return 0
  const map = loadMap()
  const next = (map[key] ?? 0) + by
  map[key] = next
  saveMap(map)
  notifyChanged()
  return next
}

export function clearUnread(contactId: string): void {
  const key = contactId.trim().toLowerCase()
  if (!key) return
  const map = loadMap()
  if (!(key in map)) return
  delete map[key]
  saveMap(map)
  notifyChanged()
}

/** Azzera unread su contactId e (se diverso) sessionToken. */
export function clearUnreadForChat(contactId: string, sessionToken?: string): void {
  clearUnread(contactId)
  const sess = sessionToken?.trim()
  if (sess && sess.toLowerCase() !== contactId.trim().toLowerCase()) {
    clearUnread(sess)
  }
}

export function clearAllUnread(): void {
  saveMap({})
  notifyChanged()
}

/**
 * Badge icona app (iOS/Android/PWA).
 * Equivalente a `PushNotifications.setBadge({ count })` (API assente in Cap 8).
 */
export async function setBadgeCount(count: number): Promise<void> {
  const { setBadge } = await import('../push/pushClient.ts')
  await setBadge({ count: Math.max(0, Math.floor(count)) })
}

async function setNativeIconBadge(count: number): Promise<void> {
  await setBadgeCount(count)
}

/** Badge sull'icona PWA/tab (Badging API) + icona nativa Capacitor. */
export function syncAppBadge(): void {
  const totalUnread = calculateTotalUnread()
  try {
    if ('setAppBadge' in Navigator.prototype) {
      if (totalUnread > 0) void navigator.setAppBadge(totalUnread)
      else void navigator.clearAppBadge()
    }
  } catch {
    /* browser non supportato */
  }
  void setNativeIconBadge(totalUnread)
}
